import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { AssetInstance, AssetInstanceCreateInput, AssetTemplate } from '@/types/assetTemplate';
import { INSTANCE_CONDITION_VALUES } from '../constants/assetConditions';
import { formatUnitCode } from '../utils/instanceNumber';
import AddressForm, { AddressFormValue } from './AddressForm';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface AssetInstanceFormProps {
  templateId: string;
  template: AssetTemplate;
  instance?: AssetInstance | null;
  nextUnitSequence?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const instanceSchema = z.object({
  instance_number: z.string().min(1, 'Unit number is required'),
  serial_number: z.string().optional(),
  current_condition: z.enum(INSTANCE_CONDITION_VALUES),
  current_status: z.enum(['available', 'reserved', 'booked', 'in_transit', 'rented', 'maintenance', 'retired', 'lost', 'damaged']),
  current_location_id: z.string().optional(),
  acquisition_date: z.string().optional(),
  acquisition_cost: z.string().optional(),
  notes: z.string().optional(),
});

type InstanceFormData = z.infer<typeof instanceSchema>;

const AssetInstanceForm: React.FC<AssetInstanceFormProps> = ({
  templateId,
  template,
  instance,
  nextUnitSequence = 1,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [step, setStep] = useState(0);
  const steps = ['Basics', 'Status', 'Location', 'Acquisition', 'Review'];
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: 'GB',
    address_type: 'warehouse',
  });
  const [newLocErrors, setNewLocErrors] = useState<Record<string, string>>({});

  const suggestedUnitCode = formatUnitCode(template.template_code, nextUnitSequence);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InstanceFormData>({
    resolver: zodResolver(instanceSchema),
    defaultValues: {
      instance_number: instance?.instance_number || suggestedUnitCode,
      serial_number: instance?.serial_number || '',
      current_condition: instance?.current_condition || template.default_condition,
      current_status: instance?.current_status || 'available',
      current_location_id: (instance as any)?.current_location_id || '',
      acquisition_date: instance?.acquisition_date
        ? new Date(instance.acquisition_date).toISOString().split('T')[0]
        : '',
      acquisition_cost: instance?.acquisition_cost ? String(instance.acquisition_cost) : '',
      notes: instance?.notes || '',
    },
  });

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (instance) return;
    setValue('instance_number', formatUnitCode(template.template_code, nextUnitSequence));
  }, [instance, template.template_code, nextUnitSequence, setValue]);

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/addresses', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.success ? data.data : data);
      }
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const submitForm = async (data: InstanceFormData) => {
    try {
      setLoading(true);

      const payload: AssetInstanceCreateInput = {
        template_id: templateId,
        instance_number: data.instance_number,
        serial_number: data.serial_number || undefined,
        current_condition: data.current_condition,
        current_status: data.current_status,
        current_location_id: (data as any).current_location_id || undefined,
        acquisition_date: data.acquisition_date || undefined,
        acquisition_cost: data.acquisition_cost ? parseFloat(data.acquisition_cost) : undefined,
        notes: data.notes || undefined,
      };

      const url = instance
        ? `/api/asset-instances/${instance.id}`
        : '/api/asset-instances';

      const method = instance ? 'PUT' : 'POST';

      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save instance');
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving instance:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to save instance');
    } finally {
      setLoading(false);
    }
  };

  const currentCondition = watch('current_condition');
  const currentStatus = watch('current_status');
  const currentLocationId = watch('current_location_id' as any);

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Wizard header */}
      <div className="flex items-center gap-3 text-sm">
        {steps.map((label, idx) => (
          <div key={label} className="flex items-center">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-white text-xs ${idx <= step ? 'bg-primary' : 'bg-gray-300'}`}>{idx + 1}</div>
            <span className={`ml-2 ${idx === step ? 'font-semibold' : 'text-gray-500'}`}>{label}</span>
            {idx < steps.length - 1 && <span className="mx-3 text-gray-400">›</span>}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="instance_number">Unit Number *</Label>
            <Input id="instance_number" {...register('instance_number')} placeholder="e.g., HB00001-0001" className={errors.instance_number ? 'border-red-500' : ''} />
            {errors.instance_number && <p className="text-sm text-red-600 mt-1">{errors.instance_number.message}</p>}
          </div>
          <div>
            <Label htmlFor="serial_number">Serial Number</Label>
            <Input id="serial_number" {...register('serial_number')} placeholder="Optional serial number" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" {...register('notes')} placeholder="Optional notes about this unit..." rows={3} />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="current_status">Status *</Label>
            <Select value={currentStatus} onValueChange={(value) => setValue('current_status', value as any)}>
              <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="rented">Rented</SelectItem>
                <SelectItem value="maintenance">In Maintenance</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            {errors.current_status && <p className="text-sm text-red-600 mt-1">{errors.current_status.message}</p>}
          </div>
          <div>
            <Label htmlFor="current_condition">Condition *</Label>
            <Select value={currentCondition} onValueChange={(value) => setValue('current_condition', value as any)}>
              <SelectTrigger><SelectValue placeholder="Select condition" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="pre_loved">Pre-Loved</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="needs_repair">Needs Repair</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            {errors.current_condition && <p className="text-sm text-red-600 mt-1">{errors.current_condition.message}</p>}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label>Location</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setAddLocOpen(true)}>Add location</Button>
            </div>
            <Select
              value={currentLocationId as any}
              onValueChange={(v) => setValue('current_location_id' as any, v === '__none__' ? (undefined as any) : (v as any))}
            >
              <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No location</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.address_line1}{loc.city ? `, ${loc.city}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Dialog open={addLocOpen} onOpenChange={setAddLocOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add location</DialogTitle>
                <DialogDescription>Enter the address details to create a new location.</DialogDescription>
              </DialogHeader>
              <AddressForm
                initial={{ country_code: 'GB', address_type: 'warehouse' }}
                onCancel={() => setAddLocOpen(false)}
                onSave={async (val: AddressFormValue) => {
                  const token = localStorage.getItem('token');
                  const res = await fetch('/api/addresses', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(val) });
                  const data = await res.json();
                  if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
                  const created = data.data;
                  setLocations((prev) => [created, ...prev]);
                  setValue('current_location_id' as any, created.id);
                  setAddLocOpen(false);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="acquisition_date">Acquisition Date</Label>
            <Input id="acquisition_date" type="date" {...register('acquisition_date')} />
          </div>
          <div>
            <Label htmlFor="acquisition_cost">Acquisition Cost</Label>
            <Input id="acquisition_cost" type="number" step="0.01" min="0" placeholder="0.00" {...register('acquisition_cost')} />
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="text-sm text-muted-foreground">Review your changes and click Save when ready.</div>
      )}

      <div className="flex justify-between">
        <div>{step > 0 && (<Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))}>Back</Button>)}</div>
        <div className="space-x-2">
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          {step < steps.length - 1 ? (
            <Button
              type="button"
              onClick={async () => {
                const stepFields: Record<number, (keyof InstanceFormData)[]> = {
                  0: ['instance_number', 'serial_number', 'notes'],
                  1: ['current_status', 'current_condition'],
                  2: ['current_location_id'],
                  3: ['acquisition_date', 'acquisition_cost'],
                };
                const fields = stepFields[step] || [];
                const ok = fields.length ? await trigger(fields as any, { shouldFocus: true }) : true;
                if (ok) setStep((s) => Math.min(steps.length - 1, s + 1));
              }}
            >
              Next
            </Button>
          ) : (
            <Button type="submit" disabled={loading}>{loading ? 'Saving...' : (instance ? 'Update Unit' : 'Create Unit')}</Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default AssetInstanceForm;
